import { useMemo, useState, useEffect } from "react";
//--------------------------------------------------------------------------------------------
import Items from "src/components/common/frame/items";
import { states } from "src/components/common/cityState";
import { onlyUnique, myAxios, getTimeStamp } from "src/components/common/functions";
import { useMyStore } from "src/components/main/mainStore";
//--------------------------------------------------------------------------------------------
export default function Leads({ tabTitle }){

    const setShowLoader = useMyStore(state => state.setShowLoader);
    const userNames = useMyStore(state => state.userNames);
    const setUserNames = useMyStore(state => state.setUserNames);
    const leads = useMyStore(state => state.leads);
    const setLeads = useMyStore(state => state.setLeads);

    const hashKeyName = "leadHashKey";
    const deleteLink = "leads/deleteLead.php";
    const readLink = "leads/readLead.php";
    const createLink = "leads/createLead.php";
    const updateLink = "leads/updateLead.php";
    const numItemsPerColumn = 10;
    const [updateDateTime, setUpdateDateTime] = useState({
        [tabTitle]: leads.updatedAt === null?getTimeStamp():leads.updatedAt,
        "userNames": userNames.updatedAt === null?getTimeStamp():userNames.updatedAt,
    });
    //--------------------------------------------------------
    const usersMap = useMemo(() => Object.fromEntries(userNames.data.map(user => [user.email, user.userName])), [userNames.data]);
    const itemsExtended = useMemo(() => leads.data.map(item => 
        ({
            ...item,
            userResponsible: usersMap[item.userResponsibleEmail]?.replace(/ {2}/g, " ") || "",
            address: [item.street, item.city, item.state, item.postalCode, item.country].filter(v => v).join(", "),
            creator: usersMap[item.creatorEmail]?.replace(/ {2}/g, " ") || "",
            updater: usersMap[item.updaterEmail]?.replace(/ {2}/g, " ") || "",
            dateCreated: item.dateCreated ?? "",
            dateUpdated: item.dateUpdated ?? ""
        })
    ), 
    [leads.data, usersMap]);
    const dataFrame = useMemo(() => [
        {
            title: "First Name",
            key: "firstName",
            type: "text",
            position: "title",
            editable: true
        },
        {
            title: "Last Name",
            key: "lastName",
            type: "text",
            position: "title",
            editable: true
        },
        {
            title: "Title",
            key: "title",
            type: "text",
            position: "title",
            editable: true
        },
        {
            title: "Organization",
            key: "organization", 
            type: "text",
            position: "title",
            editable: true
        },
        {
            title: "Business Phone",
            key: "businessPhone",
            type: "phone",
            valueType: "phone",
            position: "title",
            editable: true
        },
        {
            title: "Ext.",
            key: "extension",
            type: "text",
            position: "title",
            editable: true
        },
        {
            title: "Fax",
            key: "fax",
            type: "phone", 
            valueType: "phone", 
            position: "title",
            editable: true
        },
        {
            
            title: "Mobile",
            key: "mobile",
            type: "phone", 
            valueType: "phone",
            position: "detail",
            editable: true
        },
        {
            title: "Email",
            key: "leadEmail",
            type: "email", 
            valueType: "email",
            position: "detail",
            editable: true,
            checkDuplicate: true,
            checkDuplicateLink: "leads/checkDuplicateLead.php"
        },
        {
            title: "Website",
            key: "website",
            type: "text", 
            position: "detail",
            valueType: "link",
            editable: true
        },
        {
            title: "Street",
            key: "street",
            type: "text", 
            notFilterable: true,
            editable: true
        },
        {// value depends on state, assign in <Item/>
            title: "City",
            key: "city",
            type: "select", 
            notFilterable: true,
            editable: true
        },
        {
            title: "State",
            key: "state",
            type: "select", 
            options: states.map(value => ({label: value, value: value})),
            notFilterable: true,
            editable: true
        },
        {
            title: "Postal Code",
            key: "postalCode",
            type: "text", 
            notFilterable: true,
            editable: true
        },
        {
            title: "Country",
            key: "country",
            type: "text", 
            notFilterable: true,
            editable: true
        },
        {
            title: "Address",
            key: "address",
            type: "text", 
            position: "detail" 
        },
        {
            title: "Industry",
            key: "industry",
            type: "text",
            position: "detail",
            editable: true
        },
        {
            title: "Source",
            key: "source",
            type: "select", 
            options:[
                "Outreach", "Patrner Referral", "Phone Enquiry", "Web", "Others"
            ].map(value => ({label: value, value: value})),
            filterOptions: itemsExtended.map(u => u.source).filter(onlyUnique).map(value => ({label: value, value: value})),
            position: "detail",
            editable: true
        },
        {
            title: "Status",
            key: "status",
            type: "select",
            options:[
                "Not Contacted", "Contacted", "Converted", "Disqualified"
            ].map(value => ({label: value, value: value})),
            filterOptions: itemsExtended.map(u => u.status).filter(onlyUnique).map(value => ({label: value, value: value})),
            position: "detail",
            editable: true
        },
        {
            title: "User Resposible",
            key: "userResponsible",
            selectKey: "userResponsibleEmail",
            type: "select", 
            options: userNames.data.filter(u => u.projects !== "no").map(u => ({label: u.userName, value: u.email})),
            filterOptions: itemsExtended.map(u => u.userResponsible).filter(onlyUnique).map(value => ({label: value, value: value})), 
            valueType: "userInfo",
            userInfoName: "userResponsible",
            userInfoEmail: "userResponsibleEmail",
            position: "detail",
            editable: true
        },
        {
            title: "Referred By",
            key: "referredBy",
            type: "text", 
            position: "detail",
            editable: true
        },
        {
            title: "Creator",
            key: "creator",
            type: "select",
            filterOptions: itemsExtended.map(u => u.creator).filter(onlyUnique).map(value => ({label: value, value: value})),
            valueType: "userInfo",
            userInfoName: "creator",
            userInfoEmail: "creatorEmail",
            position: "detail",
            conditionalPosition: true,
            suffix: "dateCreated"
        },
        {
            type: "date",
            title: "Date Created",
            key: "dateCreated"
        },
        {
            title: "Updator",
            key: "updater",
            type: "select",
            filterOptions: itemsExtended.map(u => u.updater).filter(onlyUnique).map(value => ({label: value, value: value})),
            valueType: "userInfo",
            userInfoName: "updater",
            userInfoEmail: "updaterEmail",
            position: "detail",
            conditionalPosition: true,
            suffix: "dateUpdated"
        },
        {
            title: "Date Updated",
            key: "dateUpdated",
            type: "date"
        },
        {
            title: "Background",
            key: "background",
            type: "textarea",
            position: "detail",
            editable: true
        },
    ], 
    [itemsExtended, userNames.data]);
    useEffect(() => {
        if(updateDateTime["userNames"] === userNames.updatedAt)  return;
        let isMounted = true;
        const controller = new AbortController();
        const getData = async() => {
            //--------------------------------------------------------
            setShowLoader(true);
            const resUsers = await myAxios("leads/readSides.php", undefined, undefined, controller.signal);
            if(!isMounted) return;
            setShowLoader(false);
            if(!resUsers) return;
            //--------------------------------------------------------
            setUserNames(resUsers.data.users, updateDateTime["userNames"]);
        }
        getData();
        return () => {
            isMounted = false;
            setShowLoader(false);
            controller.abort();
        }
    }, [updateDateTime, userNames.updatedAt, setShowLoader, setUserNames])
    //--------------------------------------------------------
    return(
        <Items
            updateDateTime = {updateDateTime}
            setUpdateDateTime = {setUpdateDateTime}
            tabTitle = {tabTitle}
            postUrl = {updateLink}
            dataFrame = {dataFrame}
            readLink = {readLink}
            createLink = {createLink}
            deleteLink = {deleteLink}
            numItemsPerColumn = {numItemsPerColumn}
            hashKeyName = {hashKeyName}
            items = {leads}
            setItems = {setLeads}
            itemsExtended = {itemsExtended}
            defaultSortKey = {"firstName"}
            width = {"40rem"}
            minWidth = {"36rem"}
        />
    )
}